import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

// Tab selection lives in the query string so the browser's pull-to-refresh — a
// full document reload in the installed PWA — brings you back to the tab you
// were on instead of the first one.
export function useTabParam<T extends string>(
  param: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get(param)
  // A stale or hand-edited value must not reach HeroUI: a selectedKey matching
  // no Tab renders a strip with nothing selected.
  const value = allowed.includes(raw as T) ? (raw as T) : fallback

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        // The updater form matters: a page with two strips writes both params,
        // and a plain object would drop the sibling one.
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next === fallback) params.delete(param)
          else params.set(param, next)
          return params
        },
        // Replace, so Back leaves the page instead of undoing a tab tap.
        { replace: true },
      )
    },
    [param, fallback, setSearchParams],
  )

  return [value, setValue]
}
